/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 8/16/2025
 * for userx-support
 */

import {useEffect, useMemo, useState} from "react";
import {search} from "../../_utilities/reactUtils";
import {fetchTicket} from "../../_utilities/ticketUtils";
// import supportAPI if you prefer direct GETs

const currency = (n) =>
    (isNaN(n) ? 0 : n).toLocaleString(undefined, {style: "currency", currency: "USD"});

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const fmtMonth = (d) =>
    d.toLocaleDateString(undefined, {year: "numeric", month: "long"});

const addMonths = (d, delta) => new Date(d.getFullYear(), d.getMonth() + delta, 1);

function toISODate(d) {
    // YYYY-MM-DD
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

const AgentTimeManager = ({agentId}) => {
    const [monthPivot, setMonthPivot] = useState(() => startOfMonth(new Date())); // which month the list shows
    const [entries, setEntries] = useState([]); // current month entries
    const [tickets, setTickets] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [rate, setRate] = useState(0);

    // income for last 6 months (including current) for the bar chart
    const [history, setHistory] = useState([]); // [{ label, total, ym }]

    useEffect(() => {
        if (!agentId) return;

        search('contact_meta', [`t.contact_id = ${agentId}`, `meta_key = "pay_rate"`]).then(response => {
            const row = Array.isArray(response) ? response[0] : response?.[0];
            setRate(Number(row?.meta_value ?? 0));
        });

        const load = async () => {
            setError("");
            setLoading(true);
            try {
                // ----- Current month entries -----
                const from = startOfMonth(monthPivot);
                const to = endOfMonth(monthPivot);

                // Adjust field names to your schema:
                // Assuming table: time_entries
                // Fields used: agent_id, started_at (date), hours, rate (or hourly_rate)
                const rows = await search("time", [
                    `t.contact_id = ${agentId}`,
                    `t.date >= '${toISODate(from)}'`,
                    `t.date <= '${toISODate(to)}'`,
                ]);

                setEntries(rows || []);

                // collect ticket ids
                const ids = [...new Set((rows || []).map(r => r.ticket_id).filter(Boolean))];
                if(ids.length > 0){
                    const ticketRows = await search("ticket",[`t.ticket_id IN (${ids.join(',')})`]);
                    const map = {};
                    (ticketRows || []).forEach(t =>{
                        map[t.ticket_id] = t;
                    });
                    setTickets(map);
                }


                // ----- Last 6 months totals for chart (single fetch + group) -----
                const oldest = addMonths(startOfMonth(new Date()), -5);
                const newest = endOfMonth(startOfMonth(new Date()));
                const sixMonthRows = await search("time", [
                    `t.contact_id = ${agentId}`,
                    `t.date >= '${toISODate(oldest)}'`,
                    `t.date <= '${toISODate(newest)}'`,
                ]);
                // Group by YYYY-MM
                const buckets = new Map();
                sixMonthRows.forEach(r => {
                    const d = r.date ? new Date(r.date) : null;
                    if (!d || isNaN(d)) return;
                    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                    const hours = Number(r.time ?? 0) * 0.1;
                    const pay = hours * Number(rate || 0);
                    buckets.set(ym, (buckets.get(ym) || 0) + pay);
                });

                // Build last 6 months in order (fill missing with 0)
                const monthTotals = [];
                for (let i = 5; i >= 0; i--) {
                    const d = addMonths(startOfMonth(new Date()), -i);
                    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
                    monthTotals.push({
                        label: d.toLocaleDateString(undefined, {month: "short"}),
                        total: buckets.get(ym) || 0,
                        ym,
                    });
                }
                setHistory(monthTotals);

            } catch (e) {
                console.error(e);
                setError(e?.message || "Failed to load time entries.");
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [agentId, monthPivot, rate]);

    const monthTotal = useMemo(() => {
        return entries.reduce((sum, r) => {
            const hours = Number(r.time ?? 0) * 0.1;
            return sum + hours * Number(rate || 0);
        }, 0);
    }, [entries, rate]);

    const maxHistory = useMemo(
        () => Math.max(0, ...history.map((h) => h.total)),
        [history]
    );

    const goPrev = () => setMonthPivot((d) => addMonths(d, -1));
    const goNext = () => setMonthPivot((d) => addMonths(d, +1));

    return (
        <div className="wrap">
            <h3 className="mb-3">My Time</h3>

            {/* Income history mini bar chart */}
            <div className="mb-4">
                <div className="d-flex justify-content-center align-items-end" style={{gap: 8}}>
                    {history.map((h, index) => {
                        const pct = maxHistory ? Math.round((h.total / maxHistory) * 100) : 0;
                        return (
                            <div key={`chart-${index}`} className="text-center" style={{width: '15%'}}>
                                <div
                                    title={`${h.label}: ${currency(h.total)}`}
                                    style={{
                                        height:         120,
                                        display:        "flex",
                                        alignItems:     "flex-end",
                                        justifyContent: "stretch",
                                    }}
                                >
                                    <div
                                        style={{
                                            width:        '100%',
                                            height:       Math.max(4, Math.round((pct / 100) * 110)),
                                            background:   "#888",
                                            borderRadius: 4,
                                        }}
                                    />
                                </div>
                                <small className="d-block mt-1">{h.label}</small>
                            </div>
                        );
                    })}
                </div>
                <div className="text-end mt-2">
                    <small className="text-muted">Last 6 months income</small>
                </div>
            </div>

            {/* Month header + nav */}
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center" style={{gap: 8}}>
                    <button className="btn btn-sm btn-outline-secondary" onClick={goPrev}>
                        ‹ Prev
                    </button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={goNext}>
                        Next ›
                    </button>
                </div>
                <h5 className="m-0">{fmtMonth(monthPivot)}</h5>
                <div
                    style={{minWidth: 110, fontWeight: 700, textAlign: 'right', paddingRight: '10px'}}>{currency(monthTotal)}</div>
            </div>

            {/* Current month list */}
            <div className="list-group">
                {loading && <div className="list-group-item">Loading…</div>}
                {error && <div className="list-group-item text-danger">{error}</div>}
                {!loading && !error && entries.length === 0 && (
                    <div className="list-group-item text-muted">No time entries.</div>
                )}

                {!loading &&
                    !error &&
                    entries.map((e) => {
                        // Adjust field names here:
                        const id = e.time_id;
                        const startedAt = e.started_at || e.date || e.started || e.created_at;
                        const date = startedAt ? new Date(startedAt) : null;
                        const hours = Number(e.time ?? 0) * 0.1;
                        const pay = hours * rate;
                        const title = e.description || e.note || e.task || "(no description)";
                        const ticket = tickets[e.ticket_id];

                        return (
                            <div
                                key={id}
                                className="list-group-item d-flex align-items-start"
                                style={{gap: 12}}
                            >
                                <div className="flex-grow-1">
                                    <div>
                                        {ticket
                                            ? <a className={'px-1'}
                                                 title={`${ticket.ticket_id}-${ticket.title}`}
                                                 href={`/clients/${ticket.company_id}/tickets/${e.ticket_id}`}>
                                                <i role={'button'} className={'bi bi-ticket'}/>
                                            </a>
                                            : null
                                        }
                                        {title}</div>
                                    <small className="text-muted">
                                        {date ? date.toLocaleString() : "—"} · {hours.toFixed(2)}h @ {currency(rate)}/h
                                    </small>
                                </div>
                                <div style={{minWidth: 110, fontWeight: 600, textAlign: 'right'}}>{currency(pay)}</div>
                            </div>
                        );
                    })}

                {/* Total at bottom (only when not loading/error) */}
                {!loading && !error && entries.length > 0 && (
                    <div className="list-group-item d-flex align-items-center justify-content-end bg-light"
                         style={{gap: 12}}>
                        <div className="flex-grow-1">
                            <strong>Total</strong>
                        </div>
                        <div style={{minWidth: 110, fontWeight: 700, textAlign: 'right'}}>{currency(monthTotal)}</div>

                    </div>
                )}
            </div>
        </div>
    );
};

export default AgentTimeManager;
